import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Empty, Modal, Popconfirm, Spin, Table } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { OW_ASYNC_SELECT_DEFAULT_PAGINATION } from '../ow_async_select/OWAsyncSelect';
import { withUserType } from '../withUserType';
import { ComplianceType } from '../../types/ComplianceTypes';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';
import ComplianceTypeFormModal from './ComplianceTypeFormModal';

const COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION = 'complianceTypesIndex';
// The request + document forms feed their compliance-type OWAsyncSelect from this
// shared collection. Refetch it whenever a type is created/edited/deleted here so
// the dropdown reflects the change the next time a form opens.
const TYPES_DROPDOWN_TARGET_COLLECTION = 'complianceTypesDropdown';
const PAGE_SIZE = 10;
const INITIAL_PAGINATION = { current: 1, pageSize: PAGE_SIZE };

interface ComplianceTypesModalProps {
	visible: boolean;
	// Falls back to the logged-in user's role (session.userType, injected by
	// withUserType) when not explicitly provided.
	roleType?: string;
	// Injected by withUserType.
	userType?: string;
	onCancel: () => void;
	complianceTypes: ComplianceType[];
	totalComplianceTypes: number;
	fetching: boolean;
	fetchErrors: string[];
	fetchComplianceTypesPage: (pagination: any, addToTargetCollection?: boolean) => void;
	refetchTypesDropdown: () => void;
	deleteComplianceType: (complianceType: ComplianceType) => Promise<any>;
}

const ComplianceTypesModal: FC<ComplianceTypesModalProps> = ({
	visible,
	roleType,
	userType,
	onCancel,
	complianceTypes,
	totalComplianceTypes,
	fetching,
	fetchErrors,
	fetchComplianceTypesPage,
	refetchTypesDropdown,
	deleteComplianceType,
}): React.ReactElement => {
	const role = roleType || userType;
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [editingComplianceType, setEditingComplianceType] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);

	useMountEffect(() => {
		fetchComplianceTypesPage(INITIAL_PAGINATION);
	});

	const refetchFromStart = () => {
		setCurrentPage(1);
		fetchComplianceTypesPage(INITIAL_PAGINATION, false);
	};

	const openNewTypeModal = () => {
		setEditingComplianceType(null);
		setFormModalOpen(true);
	};

	const closeFormModal = () => setFormModalOpen(false);

	const handleFormSuccess = () => {
		setFormModalOpen(false);
		refetchFromStart();
		refetchTypesDropdown();
	};

	const handleDelete = (complianceType) => {
		deleteComplianceType(complianceType).then(() => {
			refetchFromStart();
			refetchTypesDropdown();
		});
	};

	const handleLoadMore = () => {
		const nextPage = currentPage + 1;
		fetchComplianceTypesPage({ current: nextPage, pageSize: PAGE_SIZE }, true).then(() => {
			setCurrentPage(nextPage);
		});
	};

	const hasMore = complianceTypes.length > 0 && complianceTypes.length < totalComplianceTypes;
	const loadingMore = fetching && complianceTypes.length > 0;
	const initialLoading = fetching && complianceTypes.length === 0;

	const renderFlag = (enabled: boolean, enabledLabel: string, disabledLabel: string) => (
		<span className="footnoteregular text-character-secondary">
			{enabled ? (
				<>
					<CheckOutlined className="mr-1" />
					{enabledLabel}
				</>
			) : (
				<>
					<CloseOutlined className="mr-1" />
					{disabledLabel}
				</>
			)}
		</span>
	);

	const renderComplianceType = (_, complianceType: ComplianceType) => (
		<div className="flex flex-col gap-1">
			<div className="h5medium">{complianceType.name}</div>
			<div className="flex flex-row items-center gap-3">
				{renderFlag(complianceType.allowSampleUpload, 'Sample upload', 'No Sample upload')}
				<span className="footnoteregular text-character-secondary">
					{complianceType.requiresUniqueDocument ? 'Unique document' : 'General document'}
				</span>
				<span className="footnoteregular text-character-secondary">
					{complianceType.isLocationSpecific ? 'Location specific' : 'All Locations'}
				</span>
			</div>
		</div>
	);

	const renderComplianceTypeActions = (_, complianceType: ComplianceType) => {
		const handleEditClick = () => {
			setEditingComplianceType(complianceType);
			setFormModalOpen(true);
		};
		const handleDeleteConfirm = () => handleDelete(complianceType);
		return (
			<div className="flex flex-row items-center justify-end gap-2">
				<Button icon={<EditOutlined />} onClick={handleEditClick} />
				<Popconfirm
					title={`Delete "${complianceType.name}"?`}
					okText="Delete"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={handleDeleteConfirm}
				>
					<Button icon={<DeleteOutlined />} />
				</Popconfirm>
			</div>
		);
	};

	const columns = [
		{
			title: 'Compliance type',
			dataIndex: 'name',
			render: renderComplianceType,
		},
		{
			title: '',
			dataIndex: 'id',
			width: 104,
			render: renderComplianceTypeActions,
		},
	];

	const renderTableFooter =
		hasMore || loadingMore
			? () => (
					<div className="flex justify-center py-2">
						{loadingMore ? (
							<Spin size="small" />
						) : (
							<Button onClick={handleLoadMore} block>
								Load More
							</Button>
						)}
					</div>
			  )
			: undefined;

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="No compliance types yet. Create one to start sending compliance requests."
		/>
	);

	const footer = (
		<div className="flex flex-row items-center justify-between">
			<Button onClick={openNewTypeModal}>+ COMPLIANCE TYPE</Button>
			<Button type="primary" onClick={onCancel}>
				DONE
			</Button>
		</div>
	);

	return (
		<Modal
			visible={visible}
			title={`Compliance Types (${totalComplianceTypes})`}
			onCancel={onCancel}
			footer={footer}
			width={587}
		>
			{fetchErrors.length > 0 && (
				<Alert className="mb-4" type="error" message={fetchErrors.join(', ')} />
			)}
			<Table
				showHeader={false}
				dataSource={complianceTypes}
				columns={columns}
				rowKey={(complianceType) => complianceType.id}
				loading={initialLoading}
				pagination={false}
				locale={{ emptyText: emptyState }}
				footer={renderTableFooter}
			/>
			{formModalOpen && (
				<ComplianceTypeFormModal
					visible={formModalOpen}
					roleType={role}
					editingComplianceType={editingComplianceType}
					onCancel={closeFormModal}
					onSuccess={handleFormSuccess}
				/>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => {
	const coll = nullSafeGetOrElse(
		COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION,
		state.compliance_types,
		{}
	);
	const ids: number[] = nullSafeGetOrElse('recordsIds', coll, []);
	const records = nullSafeGetOrElse('records', state.compliance_types, {});
	return {
		complianceTypes: ids.map((id) => records[id]).filter(Boolean),
		totalComplianceTypes: nullSafeGetOrElse('count', coll, 0),
		fetching: nullSafeGetOrElse('fetching', coll, false),
		fetchErrors: state.compliance_types.fetchErrors,
	};
};

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		fetchComplianceTypesPage: (pagination, addToTargetCollection = false) =>
			dispatch(
				complianceTypesRestCrudThunks(role).read(
					{},
					COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION,
					pagination,
					null,
					null,
					addToTargetCollection
				)
			),
		// `complianceTypesDropdown` is shared with the OWAsyncSelect pickers in the
		// new-request wizard and the supplier's add-document modal. A bare read here
		// refilled it with the endpoint's default limit of 10, truncating what those
		// pickers had loaded, so refill it the way they do — same page size, same sort.
		refetchTypesDropdown: () =>
			dispatch(
				complianceTypesRestCrudThunks(role).read(
					{},
					TYPES_DROPDOWN_TARGET_COLLECTION,
					OW_ASYNC_SELECT_DEFAULT_PAGINATION,
					{ sort_by: 'name', order: 'ascend' }
				)
			),
		deleteComplianceType: (complianceType) =>
			dispatch(complianceTypesRestCrudThunks(role).deleteImmediate(complianceType, 'id')),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ComplianceTypesModal));
